import React, { useState, useEffect } from 'react';
import { Button, TextField, Grid, Checkbox, FormControlLabel, Select, MenuItem } from '@mui/material';
import { styled } from '@mui/material/styles';
import axios from '../../utils/axios.utils';
import { useMfrSeedsContext } from '../contexts/MfrSeedsContext';

const Placeholder = styled('em')(({ theme }) => ({
  color: theme.palette.text.disabled,
  fontStyle: 'italic',
}));

const NewMfrSeedForm: React.FC<{ onSuccess: () => void, showSnackbar?: (message: string, severity: 'success' | 'error') => void }> = ({ onSuccess, showSnackbar = () => {} }) => {
  const [formValues, setFormValues] = useState({
    global_mfr_id: '',
    gtin: '',
    seed_variety_name_mfr: '',
    seed_variety_name_short: '',
    seed_variety_desc: '',
    package: '',
    crop_id: '',
    locked: false,
    active: true,
  });

  const [crops, setCrops] = useState<any[]>([]);
  const [globalMfrs, setGlobalMfrs] = useState<any[]>([]);
  const { fetchSeeds } = useMfrSeedsContext();

  useEffect(() => {
    const fetchCrops = async () => {
      try {
        const response = await axios.get('/api/admin/autotreat_mfr/crops/', {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
        });
        setCrops(response.data);
      } catch (error) {
        console.error('Error fetching crops:', error);
      }
    };

    const fetchManufacturers = async () => {
      try {
        const response = await axios.get('/api/admin/autotreat_mfr/mfrs/', {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
        });
        setGlobalMfrs(response.data);
      } catch (error) {
        console.error('Error fetching manufacturers:', error);
      }
    };

    fetchCrops();
    fetchManufacturers();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleSelectChange = (name: string, value: string) => {
    setFormValues({ ...formValues, [name]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post('/api/admin/autotreat_mfr/mfr_seeds/', formValues, {
        headers: { 
          Authorization: `Bearer ${sessionStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json',
        },
      });

      showSnackbar('Seed created successfully!', 'success');
      fetchSeeds();
      onSuccess();
    } catch (error: any) {
      console.error('Error creating seed:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to create seed. Please try again.';
      showSnackbar(errorMessage, 'error');
    }
  };

  return (
    <form id="dialog-form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Select
            value={formValues.global_mfr_id}
            onChange={(e) => handleSelectChange('global_mfr_id', e.target.value as string)}
            fullWidth
            variant="outlined"
            label="Manufacturer"
            displayEmpty
            renderValue={(selected) => {
              if (!selected) return <Placeholder>Select Manufacturer</Placeholder>;
              const selectedMfr = globalMfrs.find(mfr => mfr.global_mfr_id === selected);
              return selectedMfr?.mfr_name || selected;
            }}
          >
            <MenuItem value="" disabled>
              <Placeholder>Select Manufacturer</Placeholder>
            </MenuItem>
            {globalMfrs.map((mfr) => (
              <MenuItem key={mfr.global_mfr_id} value={mfr.global_mfr_id}>{mfr.mfr_name}</MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField name="gtin" label="GTIN" fullWidth variant="outlined" value={formValues.gtin} onChange={handleInputChange} required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField name="seed_variety_name_mfr" label="Seed Variety Name MFR" fullWidth variant="outlined" value={formValues.seed_variety_name_mfr} onChange={handleInputChange} required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField name="seed_variety_name_short" label="Seed Variety Name Short" fullWidth variant="outlined" value={formValues.seed_variety_name_short} onChange={handleInputChange} required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField name="seed_variety_desc" label="Seed Variety Desc" fullWidth variant="outlined" value={formValues.seed_variety_desc} onChange={handleInputChange} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField name="package" label="Package" fullWidth variant="outlined" value={formValues.package} onChange={handleInputChange} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Select
            value={formValues.crop_id}
            onChange={(e) => handleSelectChange('crop_id', e.target.value as string)}
            fullWidth
            variant="outlined"
            label="Crop"
            displayEmpty
            renderValue={(selected) => {
              if (!selected) return <Placeholder>Select Crop</Placeholder>;
              const selectedCrop = crops.find(crop => crop.crop_id === selected);
              return selectedCrop?.name || selected;
            }}
          >
            <MenuItem value="" disabled>
              <Placeholder>Select Crop</Placeholder>
            </MenuItem>
            {crops.map((crop) => (
              <MenuItem key={crop.crop_id} value={crop.crop_id}>{crop.name}</MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel control={<Checkbox name="locked" checked={formValues.locked} onChange={handleInputChange} />} label="Locked" />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel control={<Checkbox name="active" checked={formValues.active} onChange={handleInputChange} />} label="Active" />
        </Grid>
      </Grid>
    </form>
  );
};

export default NewMfrSeedForm;